import React from 'react'
import { CoreContainer } from '@hub/core-container'
import { Text } from '@hub/text'
import { Stack } from '@hub/stack'
import { Box } from '@hub/box'
import { H3 } from '@hub/heading'
import { Link } from '@hub/link'
import {
  PhoneMediumIcon,
  WebsiteMediumIcon,
  LocationDropPinMediumIcon,
  ParkingMediumIcon,
  InfoConciergeMediumIcon,
  HoursMediumIcon,
  GiftCardsMediumIcon,
} from '@hub/icon'
import { Grid } from '@hub/grid'
import { CentreDetails } from '@scentregroup/shared/helpers/centre'
import UtilityBoxOpeningHours from '../../components/utility-box-opening-hours'
import {
  CentreTradingSchedule,
  StoreTradingSchedule,
} from '../../helpers/parse-opening-hours/types'
import { storeIsOpen } from '../../helpers/store-helper'
import { parseStoreHours } from '../../helpers/parse-opening-hours'
import getStoreMapLink from '../../helpers/get-store-map-link'
import { isDefinedAndNotNull } from '../../lib'
import { CloudinaryImage, IMAGE_SIZES_HERO } from '../cloudinary-image'
import getGiftcardUrl from '../../helpers/get-giftcard-url'
import { StandardButton } from '../standard-button'

export interface CallsToActionCollectionProps {
  items?: readonly ({
    category?: string | null
    address?: string | null
  } | null)[]
}

interface StorefrontUtilityProps {
  centre: CentreDetails
  store: {
    sys: { id: string }
    slug: string
    title: string
    legacyIdentifier?: number | null
    telephoneNumber?: string | null
    websiteAddress?: string | null
    physicalAddress?: string | null
    landmarkDescription?: string | null
    parkingInstructions?: string | null
    paymentMethods?: readonly (string | null)[] | null
    services?: readonly (string | null)[] | null
    callsToActionCollection?: CallsToActionCollectionProps | null
    storeFrontImage?: string | null
    status: string
    retailer: {
      websiteAddress?: string | null
      paymentMethods?: readonly (string | null)[] | null
    }
  }

  centreTradingSchedule?: CentreTradingSchedule
  storeTradingSchedule?: StoreTradingSchedule
  disablePadding?: boolean
  enableH1Heading?: boolean
}

export const StorefrontUtility: React.FC<
  React.PropsWithChildren<StorefrontUtilityProps>
> = ({
  centre,
  centreTradingSchedule,
  store,
  storeTradingSchedule,
  disablePadding = false,
  enableH1Heading,
}) => {
  const {
    title,
    telephoneNumber,
    websiteAddress,
    physicalAddress,
    landmarkDescription,
    parkingInstructions,
    services,
    callsToActionCollection,
    storeFrontImage,
    status,
    retailer,
  } = store

  const parsedOpeningHours =
    storeIsOpen(status) &&
    storeTradingSchedule &&
    centreTradingSchedule &&
    centre?.timeZone &&
    parseStoreHours(
      storeTradingSchedule,
      centreTradingSchedule,
      centre.timeZone
    )

  const storeMapLink = getStoreMapLink(centre, store)

  const centreTitle = centre.title

  const paymentMethodsArr =
    store.paymentMethods && store.paymentMethods?.length > 0
      ? store.paymentMethods
      : retailer.paymentMethods
  const paymentMethods: string[] = [...(paymentMethodsArr ?? [])].filter(
    isDefinedAndNotNull
  )

  const paymentMethodsIndexOfWGC = paymentMethods.indexOf('Westfield Giftcard')
  const acceptsWestfieldGiftcard = paymentMethodsIndexOfWGC !== -1
  if (paymentMethodsIndexOfWGC !== -1) {
    paymentMethods.splice(paymentMethodsIndexOfWGC, 1)
  }
  const callsToAction = callsToActionCollection?.items ?? []

  const website = websiteAddress || retailer.websiteAddress

  return (
    <CoreContainer isMobileFullWidth>
      <Stack
        direction={['column', 'column', 'row']}
        gap="spacing-5"
        shouldWrapChildren={false}
        sx={{
          alignItems: ['left', 'left', 'top'],
          padding: disablePadding
            ? 0
            : ['spacing-mobile-margin', 'spacing-md', 'spacing-sm'],
          justifyContent: 'space-between',
        }}
        flexBasis={0}
      >
        <Stack
          sx={{ maxWidth: [undefined, undefined, '610px'] }}
          gap="spacing-md"
          shouldWrapChildren={false}
        >
          <H3
            as={enableH1Heading ? 'h1' : 'h3'}
            align={['center', 'center', 'left']}
          >
            Visit {title} at {centreTitle}
          </H3>
          <Box>
            {!storeIsOpen(status) && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HoursMediumIcon
                  boxSize="size-icon-md"
                  aria-label="Hours icon"
                />
                <Text sx={{ marginLeft: 'spacing-md' }}>{status}</Text>
              </Box>
            )}
            {storeIsOpen(status) && (
              <Box>
                {parsedOpeningHours && (
                  <UtilityBoxOpeningHours
                    openingHours={parsedOpeningHours}
                    centreSlug={centre.slug}
                  />
                )}
              </Box>
            )}
          </Box>
          {(landmarkDescription || physicalAddress) && (
            <Stack direction="row" shouldWrapChildren={true}>
              <Box sx={{ flexGrow: 0 }}>
                <LocationDropPinMediumIcon
                  sx={{ marginRight: 'spacing-md' }}
                  boxSize="size-icon-md"
                  ariaLabel="location drop pin icon"
                />
              </Box>

              <Box
                sx={{
                  maxWidth: ['size-2xl', 'size-22'],
                }}
              >
                <Box>
                  {physicalAddress && `${physicalAddress},`}{' '}
                  {landmarkDescription}
                </Box>

                {storeMapLink && (
                  <Box sx={{ marginTop: 'spacing-md' }}>
                    <Link hasUnderline isExternal href={storeMapLink}>
                      See on map
                    </Link>
                  </Box>
                )}
              </Box>
            </Stack>
          )}
          {parkingInstructions && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ParkingMediumIcon
                boxSize="size-icon-md"
                ariaLabel="Parking icon"
              />
              <Text sx={{ marginLeft: 'spacing-md' }}>
                {parkingInstructions}
              </Text>
            </Box>
          )}

          {acceptsWestfieldGiftcard && (
            <Stack gap="spacing-sm">
              <Box sx={{ display: 'flex' }}>
                <GiftCardsMediumIcon
                  boxSize="size-icon-md"
                  ariaLabel="Gift card icon"
                />
                <Box sx={{ marginLeft: 'spacing-md' }}>
                  <Link
                    hasUnderline
                    isExternal={centre.country === 'New Zealand'}
                    href={getGiftcardUrl(centre.country)}
                  >
                    Accepts Westfield Gift Cards
                  </Link>
                </Box>
              </Box>
            </Stack>
          )}

          {(telephoneNumber || websiteAddress) && (
            <Stack direction={'row'} gap={'spacing-lg'}>
              {telephoneNumber && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'spacing-md',
                  }}
                >
                  <PhoneMediumIcon
                    boxSize="size-icon-md"
                    ariaLabel="phone icon"
                  />
                  <Link hasUnderline isExternal href={`tel:${telephoneNumber}`}>
                    {telephoneNumber}
                  </Link>
                </Box>
              )}

              {website && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'spacing-md',
                  }}
                >
                  <WebsiteMediumIcon
                    boxSize="size-icon-md"
                    ariaLabel="website icon"
                  />
                  <Link hasUnderline isExternal href={website}>
                    Website
                  </Link>
                </Box>
              )}
            </Stack>
          )}

          {services?.length && (
            <Stack gap="spacing-sm" shouldWrapChildren={false}>
              <Box sx={{ display: 'flex' }}>
                <InfoConciergeMediumIcon boxSize="size-icon-md" />
                <Text sx={{ marginLeft: 'spacing-md' }}>
                  {services.join(', ')}
                </Text>
              </Box>
            </Stack>
          )}
          {callsToAction.length > 0 && (
            <Grid
              templateColumns={[
                'repeat(auto-fit, 100%)',
                'repeat(auto-fit, 100%)',
                'repeat(auto-fit, 50%)',
                'repeat(auto-fit, 49%)',
              ]}
              gap={'spacing-sm'}
              sx={{ paddingTop: 'spacing-sm' }}
            >
              {callsToAction.map((callToActionBtn, index) => {
                const { category, address } = callToActionBtn || {}
                return (
                  category &&
                  address && (
                    <StandardButton
                      key={index}
                      variant="outline"
                      href={address}
                      target="_blank"
                    >
                      {category}
                    </StandardButton>
                  )
                )
              })}
            </Grid>
          )}
        </Stack>
        {storeFrontImage && (
          <Stack
            sx={{
              display: 'flex',
              maxWidth: [undefined, undefined, 'size-22'],
            }}
          >
            <CloudinaryImage
              ratio="3/2"
              imageSetOrImage={{
                type: 'WIDE',
                url: storeFrontImage,
                alt: `${title} storefront image`,
              }}
              aria-labelledby={title}
              sizes={IMAGE_SIZES_HERO}
              sx={{ zIndex: -1 }}
            />
          </Stack>
        )}
      </Stack>
    </CoreContainer>
  )
}
